import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  claves,
  trasCambiarUsuario,
  useClienteApi,
  type AccionCuenta,
  type Concesion,
  type Invitacion,
  type Perfil,
} from "@/shared/api";

export function useUsuarios() {
  const api = useClienteApi();
  return useQuery({ queryKey: claves.usuarios, queryFn: () => api.usuarios(), staleTime: 30_000 });
}

export function useFicha(usuarioId: string) {
  const api = useClienteApi();
  return useQuery({ queryKey: claves.ficha(usuarioId), queryFn: () => api.ficha(usuarioId) });
}

/** Tras cualquier cambio en un usuario se recargan su ficha, su historial, la lista y la auditoría. */
function useAlCambiar() {
  const cache = useQueryClient();
  return (usuarioId: string) =>
    Promise.all(trasCambiarUsuario(usuarioId).map((queryKey) => cache.invalidateQueries({ queryKey })));
}

export function useInvitar() {
  const api = useClienteApi();
  const alCambiar = useAlCambiar();
  return useMutation({
    mutationFn: (i: Invitacion) => api.invitar(i),
    onSuccess: (u) => alCambiar(u.usuarioId),
  });
}

export function useAccionCuenta(usuarioId: string) {
  const api = useClienteApi();
  const alCambiar = useAlCambiar();
  return useMutation({
    mutationFn: (x: { accion: AccionCuenta; motivo: string | null }) => api.accionCuenta(usuarioId, x.accion, x.motivo),
    onSuccess: () => alCambiar(usuarioId),
  });
}

export function useConceder(usuarioId: string) {
  const api = useClienteApi();
  const alCambiar = useAlCambiar();
  return useMutation({
    mutationFn: (x: { institucion: string } & Concesion) =>
      api.conceder(usuarioId, x.institucion, { perfil: x.perfil, relacion: x.relacion, motivo: x.motivo ?? null }),
    onSuccess: () => alCambiar(usuarioId),
  });
}

export function useCambiarPerfil(usuarioId: string) {
  const api = useClienteApi();
  const alCambiar = useAlCambiar();
  return useMutation({
    mutationFn: (x: { institucion: string; perfil: Perfil }) => api.cambiarPerfil(usuarioId, x.institucion, x.perfil),
    onSuccess: () => alCambiar(usuarioId),
  });
}

export function useQuitar(usuarioId: string) {
  const api = useClienteApi();
  const alCambiar = useAlCambiar();
  return useMutation({
    mutationFn: (institucion: string) => api.quitar(usuarioId, institucion),
    onSuccess: () => alCambiar(usuarioId),
  });
}
